/**
 * Index d'empreintes F&L en mémoire (mis en cache depuis le backend) + matching
 * plus-proche-voisin (cosinus). La reconnaissance se fait 100% côté mini sur cet index.
 */
import type { AISuggestion } from '../../hooks/usePOSStore';
import { b64ToFloat32, float32ToB64, takeRecentCapture } from './produceEmbedding';
import { api } from '../api';
import { offlineDb } from '../../lib/offline/OfflineDatabase';
import { logger } from '../../utils/logger';

// posId du terminal (pour l'audit des exemples appris). Posé au login.
let POS_SOURCE: string | undefined;
export function setProduceSource(src?: string): void {
  POS_SOURCE = src;
}

// ── Périmètre F&L : rayons Fruits/Légumes (pesé OU à la pièce), PAS is_weighted ──────
// is_weighted excluait les F&L vendus à la pièce (salade, melon, chou). On scope par
// rayon : toute catégorie sous « Fruits » ou « Légumes ».
let FL_CATEGORY_IDS = new Set<string>();

const normName = (s: string): string =>
  (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

type CategorieBrute = { id: string; name: string; parent_id?: string | null };

/** Les catégories : cache local d'abord, serveur s'il est vide (vidé à la déconnexion). */
async function lireCategories(): Promise<CategorieBrute[]> {
  const locales: CategorieBrute[] = await offlineDb.categories.toArray().catch(() => []);
  if (locales.length) return locales;
  const rep: any = await api.getCategories();
  const liste = rep?.data ?? rep;
  return Array.isArray(liste) ? liste : [];
}

/** Construit le set des catégories sous les rayons Fruits/Légumes. */
export async function loadFlCategorySet(): Promise<number> {
  try {
    const cats = (await lireCategories()).map((c) => ({ ...c, parent_id: c.parent_id || null }));
    if (!cats.length) return FL_CATEGORY_IDS.size;

    const childrenOf = new Map<string | null, typeof cats>();
    for (const c of cats) {
      const k = c.parent_id;
      if (!childrenOf.has(k)) childrenOf.set(k, []);
      childrenOf.get(k)!.push(c);
    }
    // Match EXACT des rayons « Fruits » / « Légumes » — surtout PAS « Fruits secs & Graines »
    // (vrac/épicerie) ni autres rayons contenant « fruit ».
    const roots = cats.filter((c) => {
      if (c.parent_id !== null) return false;
      const n = normName(c.name).trim();
      return n === 'fruits' || n === 'fruit' || n === 'legumes' || n === 'legume';
    });
    const set = new Set<string>();
    const stack = [...roots];
    while (stack.length) {
      const c = stack.pop()!;
      if (set.has(c.id)) continue;
      set.add(c.id);
      for (const k of childrenOf.get(c.id) || []) stack.push(k);
    }
    FL_CATEGORY_IDS = set;
    logger.log(`[ProduceIndex] rayons F&L: ${set.size} catégories`);
    return set.size;
  } catch (err: any) {
    logger.log('[ProduceIndex] chargement rayons F&L échoué:', err?.message);
    return FL_CATEGORY_IDS.size;
  }
}

let flChargement: Promise<number> | null = null;

/** Charge le set des rayons F&L s'il est vide (un seul chargement à la fois). */
export function ensureFlCategorySet(): Promise<number> {
  if (FL_CATEGORY_IDS.size > 0) return Promise.resolve(FL_CATEGORY_IDS.size);
  if (!flChargement) {
    flChargement = loadFlCategorySet().finally(() => {
      flChargement = null;
    });
  }
  return flChargement;
}

/**
 * Un produit est-il un F&L (rayon Fruits/Légumes, pesé OU à la pièce) ?
 *
 * STRICT : tant que les rayons ne sont pas connus, la réponse est NON (et le chargement
 * part). Jusqu'au 26/10/2026 elle était OUI « le temps qu'il arrive » : 17 % des exemples
 * appris étaient des bocaux de vrac pesés (cookies, riz, olives), et pendant ce temps la
 * caisse pouvait proposer n'importe quel article. Pour apprendre sans attendre, voir
 * `apprendreSiFruitOuLegume`, qui patiente jusqu'au chargement.
 */
export function isFlProduct(p: { category_id?: string | null; is_weighted?: boolean }): boolean {
  if (FL_CATEGORY_IDS.size === 0) {
    void ensureFlCategorySet();
    return false;
  }
  return !!p.category_id && FL_CATEGORY_IDS.has(p.category_id);
}

interface Entry {
  product_id: string;
  vec: Float32Array;
}

let INDEX: Entry[] = [];

export function getIndexSize(): number {
  return INDEX.length;
}

// Identifiants deja presents : une sync incrementale qui repasse sur une
// empreinte deja connue ne doit pas la dupliquer en memoire.
let SEEN_IDS = new Set<string>();

/** Remplace tout l'index (sync complète). */
export function setIndexFromRows(rows: Array<{ id?: string; product_id: string; embedding: string }>): void {
  const seen = new Set<string>();
  INDEX = rows
    .map((r) => {
      try {
        if (r.id) seen.add(r.id);
        return { product_id: r.product_id, vec: b64ToFloat32(r.embedding) };
      } catch {
        return null;
      }
    })
    .filter((e): e is Entry => e !== null);
  SEEN_IDS = seen;
}

/** Ajoute des entrées (sync incrémentale). Retourne le nombre réellement ajouté. */
export function appendIndexRows(rows: Array<{ id?: string; product_id: string; embedding: string }>): number {
  let n = 0;
  for (const r of rows) {
    if (r.id && SEEN_IDS.has(r.id)) continue; // deja en memoire
    try {
      INDEX.push({ product_id: r.product_id, vec: b64ToFloat32(r.embedding) });
      if (r.id) SEEN_IDS.add(r.id);
      n++;
    } catch {
      /* vecteur illisible — ignoré */
    }
  }
  return n;
}

/** Ajout optimiste local (apprentissage immédiat, avant confirmation backend). */
export function addLocalEntry(productId: string, vec: Float32Array): void {
  INDEX.push({ product_id: productId, vec });
}

/**
 * Apprentissage auto : à l'encodage d'un F&L juste après une capture, on apprend
 * (empreinte → produit). Local immédiat + persistance/partage backend (fire-and-forget).
 * Sans capture récente → no-op (rien à apprendre).
 */
export function learnProduceFromCapture(productId: string): void {
  const vec = takeRecentCapture();
  if (!vec) return;
  apprendreEmpreinte(productId, vec);
}

/**
 * Encodage d'un article en caisse : la capture est prise TOUT DE SUITE (elle est à usage
 * unique et périme en 30 s), puis on attend de connaître les rayons pour décider. Un
 * article hors Fruits/Légumes (bocal de vrac pesé) consomme la capture sans rien apprendre.
 */
export function apprendreSiFruitOuLegume(p: { id: string; category_id?: string | null }): void {
  // Rayons déjà connus (cas courant) : un article hors F&L ne touche pas à la capture,
  // qui reste pour le légume encodé juste après.
  if (FL_CATEGORY_IDS.size > 0) {
    if (isFlProduct(p)) learnProduceFromCapture(p.id);
    return;
  }
  const vec = takeRecentCapture();
  if (!vec) return;
  ensureFlCategorySet()
    .then(() => {
      if (isFlProduct(p)) apprendreEmpreinte(p.id, vec);
    })
    .catch(() => {
      /* rayons illisibles : on n'apprend pas plutôt que d'apprendre faux */
    });
}

function apprendreEmpreinte(productId: string, vec: Float32Array): void {
  addLocalEntry(productId, vec); // disponible tout de suite sur ce mini
  api
    .addProduceEmbedding({
      product_id: productId,
      embedding: float32ToB64(vec),
      dims: vec.length,
      source: POS_SOURCE,
    })
    .then((r) => logger.log(`[ProduceIndex] appris ${productId} (kept ${r.kept})`))
    .catch((e) => logger.log('[ProduceIndex] apprentissage non persisté (offline ?):', e?.message));
}

let indexLoading = false;
let indexLoadedOnce = false;

// Horodatage serveur du dernier téléchargement : permet de ne réclamer QUE les
// empreintes ajoutées depuis (le backend accepte `?since=`).
let lastServerTime: string | null = null;

/**
 * Télécharge l'index d'empreintes COMPLET depuis le backend.
 *
 * ⚠️ Ce n'est PAS « léger » : l'index grossit à chaque encodage appris, il pesait
 * 7,1 Mo le 09/08/2026. Réservé au premier chargement de la page — pour toute
 * actualisation ultérieure, utiliser `syncProduceIndex()` (incrémental).
 * Échec silencieux (on reste sur l'ancien index).
 */
export async function loadProduceIndex(): Promise<{ total: number }> {
  indexLoading = true;
  try {
    const res = await api.getProduceEmbeddings();
    setIndexFromRows(res.embeddings);
    lastServerTime = res.serverTime || null;
    indexLoadedOnce = true;
    logger.log(`[ProduceIndex] chargé (complet): ${getIndexSize()} empreintes`);
    return { total: getIndexSize() };
  } catch (err: any) {
    logger.log('[ProduceIndex] sync échouée:', err?.message);
    return { total: getIndexSize() };
  } finally {
    indexLoading = false;
  }
}

/**
 * Actualise l'index INCRÉMENTALEMENT : ne réclame que les empreintes apparues
 * depuis le dernier téléchargement (typiquement quelques Ko, souvent zéro).
 *
 * À appeler à chaque prise de session vendeur. L'appel complet à cet endroit
 * coûtait 7,1 Mo PAR CHANGEMENT DE VENDEUR — 13 Go de données sur la seule
 * journée du 09/08/2026, et autant d'allocations mémoire dans l'onglet.
 */
export async function syncProduceIndex(): Promise<{ total: number; nouveaux: number }> {
  if (!indexLoadedOnce || !lastServerTime) {
    const r = await loadProduceIndex();
    return { total: r.total, nouveaux: r.total };
  }
  if (indexLoading) return { total: getIndexSize(), nouveaux: 0 };

  indexLoading = true;
  try {
    const res = await api.getProduceEmbeddings(lastServerTime);
    const nouveaux = appendIndexRows(res.embeddings);
    lastServerTime = res.serverTime || lastServerTime;
    if (nouveaux > 0) logger.log(`[ProduceIndex] +${nouveaux} empreinte(s) — total ${getIndexSize()}`);
    return { total: getIndexSize(), nouveaux };
  } catch (err: any) {
    logger.log('[ProduceIndex] sync incrémentale échouée:', err?.message);
    return { total: getIndexSize(), nouveaux: 0 };
  } finally {
    indexLoading = false;
  }
}

/**
 * Garantit que l'index est chargé au moins une fois pour cette session de page.
 * ⚠️ Indispensable : l'index est EN MÉMOIRE et est perdu à chaque rafraîchissement.
 * Le login ne suffit pas (un refresh ne re-switche pas le vendeur) → on recharge à la volée
 * dès qu'on tente une reconnaissance. Fire-and-forget : la 1ʳᵉ capture après refresh peut
 * rater, les suivantes matchent une fois l'index revenu (~1 s).
 */
export function ensureProduceIndexLoaded(): void {
  if (!indexLoadedOnce && !indexLoading) {
    void loadProduceIndex();
  }
}

function cosine(a: Float32Array, b: Float32Array): number {
  const n = Math.min(a.length, b.length);
  let d = 0, na = 0, nb = 0;
  for (let i = 0; i < n; i++) {
    d += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  return d / (Math.sqrt(na) * Math.sqrt(nb) + 1e-9);
}

/**
 * Plus-proche-voisin : pour chaque produit, meilleure similarité parmi ses exemples ;
 * on garde ceux au-dessus du seuil, triés, mappés sur le catalogue (produits F&L fournis).
 */
export function matchEmbedding(
  vec: Float32Array,
  products: Array<{ id: string; name: string; category_id?: string | null }>,
  opts?: { topK?: number; minSim?: number },
): AISuggestion[] {
  ensureProduceIndexLoaded(); // recharge l'index si perdu (refresh) — fire-and-forget
  if (!INDEX.length || !products.length) return [];
  const topK = opts?.topK ?? 3;
  const minSim = opts?.minSim ?? 0.82;

  const best: Record<string, number> = {};
  for (const e of INDEX) {
    const s = cosine(vec, e.vec);
    if (best[e.product_id] === undefined || s > best[e.product_id]) best[e.product_id] = s;
  }

  const pmap = new Map(products.map((p) => [p.id, p]));
  return Object.keys(best)
    .map((pid) => ({ pid, s: best[pid] }))
    .filter((x) => x.s >= minSim && pmap.has(x.pid))
    .sort((a, b) => b.s - a.s)
    .slice(0, topK)
    .map((x) => {
      const p = pmap.get(x.pid)!;
      return {
        name: p.name,
        category: '',
        confidence: Math.min(1, x.s),
        matchedProductId: p.id,
        matchedProductName: p.name,
        matchScore: x.s,
        source: 'local' as const,
      };
    });
}

/**
 * Mode VÉRIFICATION — à côté de l'identification (`matchEmbedding`), sur le même
 * index et le même cosinus.
 *
 * En caisse le moteur DEVINE parmi le catalogue ; en préparation la commande dit
 * déjà quel produit est attendu : on mesure l'image contre CE produit, et on
 * regarde si un autre lui ressemble nettement plus. La décision (OK / alerte /
 * sans référence) est prise par `utils/verificationVisuelle.ts`.
 */
export interface MesureVerification {
  /** Meilleure similarité entre la capture et les exemples appris du produit attendu (null : aucun). */
  simAttendu: number | null;
  /** Nombre d'exemples appris pour le produit attendu. */
  nbReferences: number;
  /** Le produit le plus ressemblant parmi les AUTRES, s'il y en a. */
  meilleurAutre: { productId: string; sim: number } | null;
}

export function verifyEmbedding(
  vec: Float32Array,
  produitAttenduId: string,
  index: ReadonlyArray<{ product_id: string; vec: Float32Array }> = INDEX,
): MesureVerification {
  if (index === INDEX) ensureProduceIndexLoaded();
  let simAttendu: number | null = null;
  let nbReferences = 0;
  const meilleurParProduit = new Map<string, number>();
  for (const e of index) {
    const s = cosine(vec, e.vec);
    if (e.product_id === produitAttenduId) {
      nbReferences++;
      if (simAttendu === null || s > simAttendu) simAttendu = s;
    } else {
      const prec = meilleurParProduit.get(e.product_id);
      if (prec === undefined || s > prec) meilleurParProduit.set(e.product_id, s);
    }
  }
  let meilleurAutre: MesureVerification['meilleurAutre'] = null;
  for (const [productId, sim] of meilleurParProduit) {
    if (!meilleurAutre || sim > meilleurAutre.sim) meilleurAutre = { productId, sim };
  }
  return { simAttendu, nbReferences, meilleurAutre };
}

